import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Zap, Loader2, Shuffle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'Safe Nightlife', label: 'Safe Nightlife', emoji: '🌙', color: '#FF0080' },
  { value: 'Substance Use', label: 'Substance Use', emoji: '💊', color: '#CCFF00' },
  { value: 'Peer Support', label: 'Peer Support', emoji: '🤝', color: '#00E5FF' },
  { value: 'Mental Health', label: 'Mental Health', emoji: '🧠', color: '#FFD800' },
  { value: 'Communication', label: 'Communication', emoji: '💬', color: '#FF6B6B' },
  { value: 'Harm Reduction', label: 'Harm Reduction', emoji: '🛡️', color: '#4ECDC4' },
];

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', emoji: '🟢', description: 'Clear choices, positive outcomes' },
  { value: 'medium', label: 'Medium', emoji: '🟡', description: 'Balanced challenges, realistic scenarios' },
  { value: 'hard', label: 'Hard', emoji: '🔴', description: 'Complex dilemmas, tough decisions' },
];

const GenerateScenarioModal = ({ isOpen, onClose, userId, onScenarioGenerated }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!selectedCategory || !selectedDifficulty) {
      toast.error('Please select both category and difficulty');
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await axios.post(`${API}/scenarios/generate`, null, {
        params: {
          category: selectedCategory,
          difficulty: selectedDifficulty,
          user_id: userId
        }
      });

      toast.success('🎉 New scenario generated!', {
        description: response.data.title,
        duration: 4000
      });

      onScenarioGenerated(response.data);
      onClose();
      
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate scenario', {
        description: error.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSurpriseMe = () => {
    const randomCategory = CATEGORIES[Math.floor(Math.random() * CATEGORIES.length)].value;
    const randomDifficulty = DIFFICULTIES[Math.floor(Math.random() * DIFFICULTIES.length)].value;
    
    setSelectedCategory(randomCategory);
    setSelectedDifficulty(randomDifficulty);
    
    toast('🎲 Surprise mode activated!', {
      description: `${randomCategory} • ${randomDifficulty.charAt(0).toUpperCase() + randomDifficulty.slice(1)}`,
      duration: 2000
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative bg-white rounded-3xl border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#CCFF00] border-b-4 border-black p-6 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-black" strokeWidth={3} />
              <div>
                <h2 className="text-2xl font-black text-black uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Generate Scenario
                </h2>
                <p className="text-sm text-black/70 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  AI-powered infinite learning
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-black/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-black" strokeWidth={3} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Category Selection */}
            <div>
              <label className="block text-lg font-bold text-black mb-3" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Choose Category
              </label>
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((category) => (
                  <motion.button
                    key={category.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`p-4 rounded-2xl border-4 border-black transition-all ${
                      selectedCategory === category.value
                        ? 'shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]'
                        : 'shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      backgroundColor: selectedCategory === category.value ? category.color : '#f5f5f5'
                    }}
                  >
                    <div className="text-3xl mb-2">{category.emoji}</div>
                    <div className="font-bold text-black text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {category.label}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Difficulty Selection */}
            <div>
              <label className="block text-lg font-bold text-black mb-3" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Choose Difficulty
              </label>
              <div className="space-y-3">
                {DIFFICULTIES.map((difficulty) => (
                  <motion.button
                    key={difficulty.value}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => setSelectedDifficulty(difficulty.value)}
                    className={`w-full p-4 rounded-2xl border-4 border-black text-left transition-all ${
                      selectedDifficulty === difficulty.value
                        ? 'bg-white shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]'
                        : 'bg-gray-100 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] opacity-60 hover:opacity-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{difficulty.emoji}</span>
                        <div>
                          <div className="font-bold text-black" style={{ fontFamily: 'Figtree, sans-serif' }}>
                            {difficulty.label}
                          </div>
                          <div className="text-sm text-black/70" style={{ fontFamily: 'Figtree, sans-serif' }}>
                            {difficulty.description}
                          </div>
                        </div>
                      </div>
                      {selectedDifficulty === difficulty.value && (
                        <Zap className="w-5 h-5 text-[#CCFF00]" fill="#CCFF00" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSurpriseMe}
                disabled={isGenerating}
                className="flex-1 bg-white text-black border-4 border-black font-bold px-6 py-4 rounded-2xl hover:bg-gray-100 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                <Shuffle className="w-5 h-5 mr-2" />
                Surprise Me!
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !selectedCategory || !selectedDifficulty}
                className="flex-1 bg-[#CCFF00] text-black border-4 border-black font-bold px-6 py-4 rounded-2xl hover:bg-[#CCFF00]/90 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Scenario
                  </>
                )}
              </Button>
            </div>

            {/* Info */}
            {isGenerating && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#00E5FF]/10 border-2 border-[#00E5FF] rounded-2xl p-4"
              >
                <p className="text-sm text-black/80 text-center" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  ✨ Creating your personalized scenario... This may take 10-15 seconds
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default GenerateScenarioModal;
